import collect from 'collect.js';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FavEvent } from '../../../py-generated/datalouder/favorite_stock/types';
import { PriceDataPoint } from '../../../types';
import {
  ExternalMarkerGenerator,
  ExternalMarkerGeneratorGetMarkers,
} from './base';

const MarksTooltip: React.FC<{ action: string }> = ({ action }) => {
  const [t] = useTranslation();
  return <div>{t(action)}</div>;
};

interface FavoriteMark {
  date: string;
  action:
    | 'felt-bearish'
    | 'felt-bullish'
    | 'not-felt-bullish'
    | 'not-felt-bearish';
}

const makeData = (
  action: FavoriteMark['action'],
  date: string,
  dataPoint: PriceDataPoint
) => {
  const bearish = action.endsWith('bearish');
  const cancelled = action.startsWith('not-');
  const fill = cancelled ? '#ffffff' : bearish ? '#EFBE7D' : '#82A3FF';
  const stroke = bearish ? '#FF8200' : '#2E67F8';

  return {
    name: action,
    coord: [date, bearish ? dataPoint.high : dataPoint.low],
    symbol: 'arrow',
    symbolSize: [10, 10],
    symbolRotate: bearish ? 180 : 0,
    symbolOffset: [
      0,
      cancelled ? (bearish ? '-200%' : '200%') : bearish ? '-100%' : '100%',
    ],
    itemStyle: {
      color: fill,
      borderColor: stroke,
    },
  };
};

class Favorites extends ExternalMarkerGenerator {
  marks: FavoriteMark[];

  process = (favEvents: FavEvent[]): void => {
    for (const favEvent of favEvents) {
      const action = favEvent.unlike
        ? favEvent.removing
          ? 'not-felt-bearish'
          : 'felt-bearish'
        : favEvent.removing
          ? 'not-felt-bullish'
          : 'felt-bullish';

      const dateStr = favEvent.price_date ?? favEvent.date;
      this.marks.push({
        date:
          dateStr.slice(0, 4) +
          '-' +
          dateStr.slice(4, 6) +
          '-' +
          dateStr.slice(6, 8),
        action,
      });
    }
  };

  constructor(favEvents: FavEvent[]) {
    super();
    this.marks = [];
    this.process(favEvents);
  }

  tooltipCreater = (
    name: string // action
  ): React.ReactElement => {
    return React.createElement(MarksTooltip, {
      action: name,
    });
  };

  getMarkers(
    dataPoints: ReadonlyArray<PriceDataPoint>
  ): ExternalMarkerGeneratorGetMarkers {
    const marksWithCoordDate = this.marks.map(mark => ({
      ...mark,
      coordDate: mark.date,
    }));
    const marksByDate = collect(marksWithCoordDate).groupBy('coordDate').all();
    const data = [];
    for (const dataPoint of dataPoints) {
      const date = String(dataPoint.date);
      const marksOnDate: FavoriteMark[] = marksByDate[date]?.items;
      if (marksOnDate) {
        const actions = new Set(marksOnDate.map(({ action }) => action));
        for (const action of actions) {
          data.push(makeData(action, date, dataPoint));
        }
      }
    }

    return { data, tooltipCreater: this.tooltipCreater };
  }
}

export default Favorites;
